<template>
  <div class="h-full p-[2.5rem] flex flex-col">
    <!-- 진행 단계 표시기 -->
    <div class="mt-[3rem] mb-[4rem]">
      <div class="flex items-center justify-between max-w-2xl mx-auto">
        <!-- Step 1 -->
        <div class="flex items-center flex-1">
          <div :class="[
            'w-10 h-10 rounded-full flex items-center justify-center font-bold text-base transition-colors',
            getCurrentStepNumber() >= 1 ? 'bg-primary text-white' : 'bg-dark-700 text-gray-500'
          ]">
            1
          </div>
          <div class="ml-3">
            <p :class="['text-base font-medium whitespace-nowrap', getCurrentStepNumber() >= 1 ? 'text-white' : 'text-gray-500']">
              회사명 입력 & 파일 업로드
            </p>
          </div>
        </div>
        
        <!-- 연결선 -->
        <div :class="[
          'flex-1 h-1 mx-4',
          getCurrentStepNumber() >= 2 ? 'bg-primary' : 'bg-dark-700'
        ]"></div>
        
        <!-- Step 2 -->
        <div class="flex items-center flex-1">
          <div :class="[
            'w-10 h-10 rounded-full flex items-center justify-center font-bold text-base transition-colors',
            getCurrentStepNumber() >= 2 ? 'bg-primary text-white' : 'bg-dark-700 text-gray-500'
          ]">
            2
          </div>
          <div class="ml-3">
            <p :class="['text-base font-medium', getCurrentStepNumber() >= 2 ? 'text-white' : 'text-gray-500']">
              리스크 분석 결과
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- 분석 입력 폼 -->
    <div v-if="!store.analysisResults" class="flex-1 flex flex-col overflow-hidden h-full">
      
      <!-- 입력 영역 (회사명 + 파일 업로드) -->
      <div class="flex-1 flex flex-col overflow-hidden min-h-0 card">
        <!-- 회사명 입력 -->
        <div class="mb-8 flex-shrink-0">
          <label class="input-label">
            <i class="bi bi-building mr-2 text-primary"></i>
            투자대상기업명
          </label>
          <input
            v-model="companyName"
            type="text"
            placeholder="투자대상기업명을 입력하세요(예: 삼성전자)"
            class="input w-full"
          />
        </div>

        <!-- 파일 업로드 -->
        <div class="flex-1 flex flex-col overflow-hidden min-h-0">
          <label class="input-label flex-shrink-0">
            <i class="bi bi-cloud-arrow-up mr-2 text-primary"></i>
            파일 업로드
          </label>
          
          <div class="border border-dark-600 rounded-lg p-6 flex-1 flex flex-col overflow-hidden min-h-0 gap-4">
            <!-- 파일 드래그 영역 -->
            <div
              @drop="handleDrop"
              @dragover.prevent
              @dragenter.prevent
              @dragleave="isDragging = false"
              :class="[
                'border-2 border-dashed rounded-lg p-8 py-16 text-center cursor-pointer transition-colors duration-200 flex-1 flex items-center justify-center min-h-0',
                isDragging
                  ? 'border-primary bg-dark-700'
                  : 'border-dark-600 hover:border-primary'
              ]"
              @click="$refs.fileInput.click()"
              @dragenter="isDragging = true"
            >
              <div>
                <i class="bi bi-file-earmark-pdf text-4xl text-gray-400 mb-3 block"></i>
                <p class="text-lg font-bold text-gray-200 mb-1">투자상품설명서</p>
                <p class="text-primary text-sm mb-1">파일을 드래그 하거나 클릭하여 업로드 해주세요</p>
                <p class="text-caption mt-4">PDF 형식의 파일을 선택해 주세요</p>
              </div>
            </div>

            <input
              ref="fileInput"
              type="file"
              accept=".pdf"
              style="display: none"
              @change="handleFileSelect"
            />

            <!-- 선택된 파일 -->
            <div v-if="selectedFile" class="bg-dark-700 border border-dark-600 rounded-lg p-6 flex-1 flex flex-col min-h-0">
              <div class="flex items-center justify-between mb-3 flex-shrink-0">
                <p class="text-sm text-gray-400">
                  선택된 파일
                </p>
              </div>
              <div class="w-full flex items-center justify-between">
                <div class="flex items-center gap-4">
                  <div class="flex-shrink-0 w-12 h-12 rounded-full bg-red-900 bg-opacity-30 flex items-center justify-center">
                    <i class="bi bi-file-pdf text-red-400 text-2xl"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-white text-base font-semibold mb-1">{{ selectedFile.name }}</p>
                    <p class="text-gray-400 text-sm">{{ (selectedFile.size / 1024 / 1024).toFixed(2) }} MB</p>
                  </div>
                </div>
                <button
                  @click="selectedFile = null"
                  class="text-gray-400 hover:text-red-500 transition-colors ml-3 flex-shrink-0"
                >
                  <i class="bi bi-trash text-xl"></i>
                </button>
              </div>
            </div>
            <div v-else class="bg-dark-700 border border-dark-600 rounded-lg p-6 flex-1 flex items-center justify-center min-h-0">
              <div class="text-center">
                <i class="bi bi-inbox text-gray-600 text-4xl mb-2 block"></i>
                <p class="text-gray-500 text-sm">선택된 파일이 없습니다</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 버튼들 -->
      <div class="flex-shrink-0 mt-8">
        <button
          :disabled="store.isLoading || !companyName || !selectedFile"
          @click="handleAnalyzeRisk"
          class="w-full btn-primary mb-3"
        >
          <span v-if="!store.isLoading">
            <i class="bi bi-play-fill mr-2"></i>리스크 분석 시작
          </span>
          <span v-else>
            <i class="bi bi-hourglass-split mr-2 animate-pulse"></i>
            분석 중...
          </span>
        </button>

        <button
          :disabled="store.isLoading"
          @click="handleLoadTestData"
          class="w-full btn-secondary mb-4"
        >
          <i class="bi bi-file-earmark-arrow-down mr-2"></i>
          검증 데이터 로드하기
        </button>

        <div v-if="store.error" class="mt-4 p-4 bg-red-900 bg-opacity-30 border border-red-500 rounded-lg text-red-400 text-sm">
          {{ store.error }}
        </div>
      </div>
    </div>

    <!-- 분석 결과 -->
    <div v-else>
      <RiskAnalysisResult />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRiskAnalysisStore } from '@/stores/riskAnalysis';
import { riskAnalysisService } from '@/services/riskAnalysis';
import RiskAnalysisResult from '@/components/risk-analysis/RiskAnalysisResult.vue';
import { useToast } from 'vue-toastification';

const store = useRiskAnalysisStore();
const toast = useToast();

const companyName = ref('');
const selectedFile = ref(null);
const isDragging = ref(false);
const fileInput = ref(null);

const getCurrentStepNumber = () => {
  if (store.analysisResults) {
    return 2; // 리스크 분석 결과
  } else {
    return 1; // 회사명 입력 및 파일 업로드
  }
};

const handleFileSelect = (event) => {
  const files = event.target.files;
  if (files.length > 0) {
    selectedFile.value = files[0];
  }
};

const handleDrop = (event) => {
  isDragging.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    selectedFile.value = files[0];
  }
};

const handleAnalyzeRisk = async () => {
  if (!companyName.value) {
    toast.error('회사명을 입력해주세요');
    return;
  }

  if (!selectedFile.value) {
    toast.error('파일을 선택해주세요');
    return;
  }

  // 분석 시작 안내 메시지
  toast.info('AI 분석을 시작합니다. 분석에는 최대 10분 정도 소요될 수 있습니다.', {
    timeout: 5000,
  });

  store.setLoading(true);
  store.setError(null);

  try {
    const response = await riskAnalysisService.analyzeCompanyRisk(
      companyName.value,
      selectedFile.value
    );

    if (response.data) {
      store.setAnalysisResults(response.data);
      toast.success('분석이 완료되었습니다.');
    }
  } catch (error) {
    console.error(error);
    store.setError('분석 중 오류가 발생했습니다. 다시 시도해주세요.');
    toast.error('분석 실패');
  } finally {
    store.setLoading(false);
  }
};

const handleLoadTestData = async () => {
  try {
    // import로 JSON 파일 직접 가져오기
    const testData = await import('@/assets/results/risk_result.json');
    store.setAnalysisResults(testData.default);
    toast.success('검증 데이터가 로드되었습니다.');
  } catch (error) {
    console.error(error);
    toast.error('데이터 로드 실패: ' + error.message);
  }
};

</script>

<style scoped>
</style>
